import { useCallback, useEffect, useRef, useState } from 'react';
import catchCancel from 'helpers/catchCancel';
import invoiceApi from '../helpers/invoiceApi';
import { InvoiceDetails } from '../types';

export interface UseInvoiceDetailsReturn {
  invoiceDetails?: InvoiceDetails;
  isLoading: boolean;
  errors?: Error | null;
  refetch: () => void;
}

/**
 * Lazily fetches the full detail for the selected invoice via the existing
 * `invoice` axios resource (`invoiceApi.fetch(invoiceId)`), mirroring the legacy
 * `InvoiceView` fetch. Skips entirely when `invoiceId === null` (no row selected
 * / panel closed) and cancels any in-flight request when the id changes or the
 * component unmounts. Returns `refetch` so the panel can refresh after a tag
 * update.
 */
const useInvoiceDetails = (
  invoiceId: string | null
): UseInvoiceDetailsReturn => {
  const [invoiceDetails, setInvoiceDetails] = useState<
    InvoiceDetails | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(false);
  // Keyed by the invoice it belongs to, so an error from the previous selection
  // is not reported against the next one before its own fetch has started.
  const [failedFetch, setFailedFetch] = useState<{
    invoiceId: string;
    error: Error;
  } | null>(null);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchDetails = useCallback((): (() => void) | undefined => {
    if (!invoiceId) {
      setInvoiceDetails(undefined);
      setIsLoading(false);
      setFailedFetch(null);
      return undefined;
    }

    setIsLoading(true);
    setFailedFetch(null);

    const { promise, cancel } = invoiceApi.fetch(invoiceId);
    promise
      .then(({ data }: { data: InvoiceDetails }) => {
        if (!isMounted.current) return;
        setInvoiceDetails(data);
        setIsLoading(false);
      })
      .catch(catchCancel)
      .catch((err: Error) => {
        if (!isMounted.current) return;
        // Otherwise the previously selected invoice's details would render
        // under the new selection once the panel stops waiting.
        setInvoiceDetails(undefined);
        setFailedFetch({ invoiceId, error: err });
        setIsLoading(false);
      });

    return cancel;
  }, [invoiceId]);

  useEffect(() => {
    const cancel = fetchDetails();
    return () => {
      if (typeof cancel === 'function') cancel();
    };
  }, [fetchDetails]);

  const errors =
    failedFetch?.invoiceId === invoiceId ? failedFetch.error : null;

  return { invoiceDetails, isLoading, errors, refetch: fetchDetails };
};

export default useInvoiceDetails;
